import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../../../services/api';
import { API_ENDPOINTS } from '../../../config/api';

// Public "units sold" counter — no login required. Auth is a fixed service
// mobile (not a real admin's own number) so this page can hit the same
// admin-scoped endpoint the Orders tab uses, without requiring a session.
const SERVICE_ADMIN_MOBILE = '6281507658';
const REFRESH_MS = 5000;

// Celebrate every 100 units sold.
const MILESTONE_STEP = 100;

// Milestone celebration: continuous crackers + falling confetti for ~30s (3 x 10s segments).
const CELEBRATION_SEGMENT_MS = 10000;
const CELEBRATION_SEGMENT_COUNT = 3;
const CELEBRATION_DURATION_MS = CELEBRATION_SEGMENT_COUNT * CELEBRATION_SEGMENT_MS;
const SPAWN_TICK_MS = 220;
const BURST_PARTICLES_PER_TICK = 3; // per corner, per tick
const FALL_PARTICLES_PER_TICK = 5;
const CONFETTI_COLORS = ['#f472b6', '#38bdf8', '#fbbf24', '#34d399', '#a78bfa', '#ffffff'];

type ConfettiShape = 'square' | 'circle' | 'star' | 'dash';
const CONFETTI_SHAPES: ConfettiShape[] = ['square', 'circle', 'star', 'dash'];
const randomShape = () => CONFETTI_SHAPES[Math.floor(Math.random() * CONFETTI_SHAPES.length)];
const randomColor = () => CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];

type Corner = 'bl' | 'br';
const CORNERS: Corner[] = ['bl', 'br'];

// Where each corner's popper/particles are anchored, and which direction (sign)
// they should fly in — both bottom corners point inward and up, toward the center/top of the screen.
const CORNER_ANCHOR: Record<Corner, React.CSSProperties> = {
    bl: { bottom: 0, left: 0 },
    br: { bottom: 0, right: 0 },
};
const CORNER_DIRECTION: Record<Corner, { x: number; y: number }> = {
    bl: { x: 1, y: -1 },
    br: { x: -1, y: -1 },
};
// Rotates each corner's popper emoji so it visually points inward, toward the center.
const CORNER_POPPER_ROTATION: Record<Corner, number> = {
    bl: -45,
    br: -135,
};

interface BurstPiece {
    id: string;
    createdAt: number;
    corner: Corner;
    tx: number; // vw travelled, signed per corner direction — strongly biased toward screen center
    ty: number; // vh travelled, signed per corner direction — reaches past the top of the screen
    rot: number;
    color: string;
    shape: ConfettiShape;
    duration: number;
    delay: number;
    size: number;
}

interface FallPiece {
    id: string;
    createdAt: number;
    left: number; // 0-100%, across the full width
    color: string;
    shape: ConfettiShape;
    duration: number;
    delay: number;
    rotate: number;
    size: number;
}

// One small batch per spawn tick — biased strongly inward/upward so the trajectory
// clearly converges toward the center of the screen rather than just drifting outward.
const makeBurstBatch = (): BurstPiece[] => {
    const now = Date.now();
    return CORNERS.flatMap((corner) => {
        const dir = CORNER_DIRECTION[corner];
        return Array.from({ length: BURST_PARTICLES_PER_TICK }, (_, i) => ({
            id: `${now}-${corner}-${i}-${Math.random()}`,
            createdAt: now,
            corner,
            tx: dir.x * (35 + Math.random() * 35),
            ty: dir.y * (45 + Math.random() * 65),
            rot: Math.random() * 720 - 360,
            color: randomColor(),
            shape: randomShape(),
            duration: 1.8 + Math.random() * 1.2,
            delay: Math.random() * 0.15,
            size: 10 + Math.random() * 10,
        }));
    });
};

// Classic confetti raining continuously from just above the top edge, across the full width.
const makeFallBatch = (): FallPiece[] => {
    const now = Date.now();
    return Array.from({ length: FALL_PARTICLES_PER_TICK }, (_, i) => ({
        id: `${now}-fall-${i}-${Math.random()}`,
        createdAt: now,
        left: Math.random() * 100,
        color: randomColor(),
        shape: randomShape(),
        duration: 2.6 + Math.random() * 1.8,
        delay: Math.random() * 0.15,
        rotate: Math.random() * 360,
        size: 8 + Math.random() * 8,
    }));
};

const pruneExpired = <T extends { createdAt: number; delay: number; duration: number }>(pieces: T[], now: number): T[] =>
    pieces.filter((p) => p.createdAt + (p.delay + p.duration) * 1000 > now);

// Renders one confetti particle's visual (star / dot / streamer dash / square) — the
// outer wrapper (positioned + animated) is kept separate so this never fights the burst/fall transform.
const ConfettiShapeVisual: React.FC<{ shape: ConfettiShape; color: string; size: number }> = ({ shape, color, size }) => {
    switch (shape) {
        case 'circle':
            return <span className="block rounded-full" style={{ width: size, height: size, backgroundColor: color }} />;
        case 'star':
            return <span className="block leading-none" style={{ color, fontSize: size * 2 }}>★</span>;
        case 'dash':
            return <span className="block rounded-full" style={{ width: size * 0.4, height: size * 2.2, backgroundColor: color }} />;
        case 'square':
        default:
            return <span className="block rounded-sm" style={{ width: size, height: size * 1.3, backgroundColor: color }} />;
    }
};

const LiveUnitsPage: React.FC = () => {
    const [units, setUnits] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    const [celebrating, setCelebrating] = useState(false);
    const [burstConfetti, setBurstConfetti] = useState<BurstPiece[]>([]);
    const [fallConfetti, setFallConfetti] = useState<FallPiece[]>([]);
    // The milestone-step multiple we've last seen — null until the first successful
    // fetch, so we never "celebrate" the very first render.
    const lastMilestoneRef = useRef<number | null>(null);

    const fetchUnits = useCallback(async () => {
        try {
            const response = await api.get(API_ENDPOINTS.getPendingUnits(), {
                headers: { 'X-Admin-Mobile': SERVICE_ADMIN_MOBILE },
                params: { page: 1, page_size: 1 },
            });
            const count = response.data?.total_units_approved_count;
            if (count !== undefined && count !== null) {
                const numericCount = Number(count);
                setUnits(numericCount);
                setError(null);

                const milestone = Math.floor(numericCount / MILESTONE_STEP);
                if (lastMilestoneRef.current !== null && milestone > lastMilestoneRef.current) {
                    setCelebrating(true);
                }
                lastMilestoneRef.current = milestone;
            }
            setLastUpdated(new Date());
        } catch (err) {
            setError('Unable to refresh');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUnits();
        const interval = setInterval(fetchUnits, REFRESH_MS);
        return () => clearInterval(interval);
    }, [fetchUnits]);

    // Manual preview — open the page with ?celebrate=1 to trigger the celebration
    // immediately, without waiting for a real milestone to be crossed.
    useEffect(() => {
        if (new URLSearchParams(window.location.search).get('celebrate') === '1') {
            setCelebrating(true);
        }
    }, []);

    // Continuously spawn new burst + fall particles for the full celebration window (pruning
    // expired ones each tick), instead of firing a few one-off waves with dead gaps between them.
    useEffect(() => {
        if (!celebrating) return;
        const spawn = () => {
            const now = Date.now();
            setBurstConfetti((prev) => [...pruneExpired(prev, now), ...makeBurstBatch()]);
            setFallConfetti((prev) => [...pruneExpired(prev, now), ...makeFallBatch()]);
        };
        spawn();
        const spawnInterval = setInterval(spawn, SPAWN_TICK_MS);
        const stopTimer = setTimeout(() => {
            clearInterval(spawnInterval);
            setCelebrating(false);
            setBurstConfetti([]);
            setFallConfetti([]);
        }, CELEBRATION_DURATION_MS);
        return () => {
            clearInterval(spawnInterval);
            clearTimeout(stopTimer);
        };
    }, [celebrating]);

    // Best-effort deterrent only — a real browser DevTools/network panel can't actually
    // be blocked client-side; this just discourages casual right-click/shortcut access.
    useEffect(() => {
        const blockContextMenu = (e: MouseEvent) => e.preventDefault();
        const blockDevToolsKeys = (e: KeyboardEvent) => {
            const key = e.key?.toUpperCase();
            const blocked =
                key === 'F12' ||
                ((e.ctrlKey || e.metaKey) && e.shiftKey && (key === 'I' || key === 'J' || key === 'C')) ||
                ((e.ctrlKey || e.metaKey) && key === 'U');
            if (blocked) e.preventDefault();
        };
        document.addEventListener('contextmenu', blockContextMenu);
        document.addEventListener('keydown', blockDevToolsKeys);
        return () => {
            document.removeEventListener('contextmenu', blockContextMenu);
            document.removeEventListener('keydown', blockDevToolsKeys);
        };
    }, []);

    const formattedUnits = units !== null ? units.toLocaleString('en-IN') : '—';
    const lastUpdatedLabel = lastUpdated
        ? lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
        : '';

    return (
        <div className="min-h-full font-outfit bg-slate-950 text-white flex flex-col overflow-hidden relative">
            {/* Ambient brand-green glow */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[140%] h-[80%] bg-emerald-500/10 blur-[120px] rounded-full" />
            </div>

            {/* Milestone celebration — crackers at the bottom corners continuously blast confetti
                (stars/dots/streamers/squares) up toward the center of the screen, while classic
                confetti continuously rains in from just above the top edge. Runs ~30s straight. */}
            {celebrating && (
                <div className="pointer-events-none fixed inset-0 z-20 overflow-hidden">
                    {CORNERS.map((corner) => (
                        <span
                            key={corner}
                            className="absolute text-5xl md:text-7xl animate-popIn origin-center"
                            style={{ ...CORNER_ANCHOR[corner], transform: `rotate(${CORNER_POPPER_ROTATION[corner]}deg)` }}
                        >
                            🎉
                        </span>
                    ))}
                    {burstConfetti.map((c) => (
                        <span
                            key={c.id}
                            className="absolute animate-confettiBurst"
                            style={{
                                ...CORNER_ANCHOR[c.corner],
                                animationDuration: `${c.duration}s`,
                                animationDelay: `${c.delay}s`,
                                ['--tx' as any]: `${c.tx}vw`,
                                ['--ty' as any]: `${c.ty}vh`,
                                ['--rot' as any]: `${c.rot}deg`,
                            }}
                        >
                            <ConfettiShapeVisual shape={c.shape} color={c.color} size={c.size} />
                        </span>
                    ))}
                    {fallConfetti.map((f) => (
                        <span
                            key={f.id}
                            className="absolute top-0 animate-confettiFall"
                            style={{
                                left: `${f.left}%`,
                                animationDuration: `${f.duration}s`,
                                animationDelay: `${f.delay}s`,
                                transform: `rotate(${f.rotate}deg)`,
                            }}
                        >
                            <ConfettiShapeVisual shape={f.shape} color={f.color} size={f.size} />
                        </span>
                    ))}
                </div>
            )}

            {/* Body — AnimalKart / count / "Units Sold", all centered together on the page */}
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 md:px-6 text-center">
                <span className="text-4xl md:text-7xl font-black tracking-tighter mb-6 md:mb-8">AnimalKart</span>

                <div
                    key={units ?? 'loading'}
                    className={`text-[7rem] leading-none sm:text-[16rem] md:text-[21rem] font-black tracking-tight tabular-nums animate-in fade-in zoom-in-95 duration-500 mb-6 md:mb-8 ${celebrating ? 'animate-celebrationPulse' : ''}`}
                >
                    {loading && units === null ? (
                        <span className="text-slate-700">···</span>
                    ) : (
                        <span className="bg-gradient-to-b from-white to-emerald-200 bg-clip-text text-transparent drop-shadow-sm">
                            {formattedUnits}
                        </span>
                    )}
                </div>

                <p className="text-white text-xl md:text-2xl font-bold max-w-md">
                    Units Sold
                </p>
            </div>

            {/* Footer status bar */}
            <div className="relative z-10 flex items-center justify-center gap-4 pb-6 md:pb-8 text-sm md:text-base font-semibold text-white">
                <span className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${error ? 'bg-red-400' : 'bg-emerald-400'}`} />
                    {error ? error : 'Live'}
                </span>
                {lastUpdatedLabel && <span>Updated {lastUpdatedLabel}</span>}
            </div>
        </div>
    );
};

export default LiveUnitsPage;
